import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, ClockIcon, MapPinIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { LOCATIONS } from '../data/locations';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/81863cf8-fe5b-47f4-8510-439876f3cde4.jpg';
function toIntlNumber(phone: string) {
  const digits = phone.replace(/\D/g, '');
  return digits.startsWith('0') ? `+61${digits.slice(1)}` : digits;
}
export function Contact() {
  const [slug, setSlug] = useState(LOCATIONS[0].slug);
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);
  const location = LOCATIONS.find(l => l.slug === slug) ?? LOCATIONS[0];
  const message = `Hi ${location.name}, ${body || '[your question]'}${name ? ` — ${name}` : ''}`;
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    window.location.href = `sms:${toIntlNumber(location.phone)}?&body=${encodeURIComponent(message)}`;
    setSent(true);
  }
  return <main className="w-full bg-ink-950" data-id="element-190" data-mp="19enwyz9ri315">
      <PageHero image={HERO_IMAGE} eyebrow="Say Hello" title="Contact Us" subtitle="Reach the location directly. We're here to answer anything you'd like to ask." data-id="element-191" data-mp="1e7h2vqgyvhet" />

      <section aria-label="Contact details" className="glow-warm w-full bg-ink-950" data-id="element-192" data-mp="mhchbohh0oc0">
        <div className="mx-auto grid w-full max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2" data-id="element-193" data-mp="qhmqfa0k7uv3">
          <div data-id="element-194" data-mp="vgpv2tcmnz2j">
            <h2 className="text-3xl font-bold tracking-tight text-white" data-id="element-195" data-mp="vg7qn9ftaejj">Send us a message</h2>
            <p className="mt-3 text-neutral-400" data-id="element-196" data-mp="10glyaqdi3mh1">
              Pick a venue and we'll open a pre-filled text message to that shop.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-id="element-197" data-mp="26yiooaf60b7b">
              <div data-id="element-198" data-mp="2btxwakmkxava">
                <label htmlFor="location" className="block text-sm font-semibold text-white" data-id="element-199" data-mp="2bxls5qfhy9d6">
                  Venue
                </label>
                <select id="location" name="location" value={slug} onChange={e => {
                setSlug(e.target.value);
                setSent(false);
              }} className="mt-2 w-full rounded-lg border border-white/10 bg-ink-850 px-4 py-3.5 text-white focus:border-lotus-500 focus:outline-none" data-id="element-200" data-mp="1h8sbheff0vrg">
                  {LOCATIONS.map(loc => <option key={loc.slug} value={loc.slug} data-id="element-201" data-mp="1d6owiftjf91f">
                      {loc.suburb} — {loc.name}
                    </option>)}
                </select>
              </div>

              <div data-id="element-202" data-mp="kqe6mf9b80vc">
                <label htmlFor="name" className="block text-sm font-semibold text-white" data-id="element-203" data-mp="kpumqt03n2eo">
                  Name
                </label>
                <input id="name" name="name" value={name} onChange={e => setName(e.target.value)} required placeholder="Your name" className="mt-2 w-full rounded-lg border border-white/10 bg-ink-850 px-4 py-3.5 text-white placeholder:text-neutral-500 focus:border-lotus-500 focus:outline-none" data-id="element-204" data-mp="fqru681b2onl" />
              </div>

              <div data-id="element-205" data-mp="3a4mlej5xl5sq">
                <label htmlFor="message" className="block text-sm font-semibold text-white" data-id="element-206" data-mp="3a65wzwgn6so6">
                  Message
                </label>
                <textarea id="message" name="message" rows={4} value={body} onChange={e => setBody(e.target.value)} required placeholder="How can we help?" className="mt-2 w-full resize-y rounded-lg border border-white/10 bg-ink-850 px-4 py-3.5 text-white placeholder:text-neutral-500 focus:border-lotus-500 focus:outline-none" data-id="element-207" data-mp="3f1v3ykuzrbui" />
              </div>

              <div className="rounded-xl border border-white/5 bg-ink-850 p-5" data-id="element-208" data-mp="257asyzxj5my5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-lotus-500" data-id="element-209" data-mp="2557s1kuua9en">
                  Message preview
                </p>
                <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-210" data-mp="1v9wdp3ncf47f">{message}</p>
                <p className="mt-3 text-xs text-neutral-500" data-id="element-211" data-mp="2sxeqqqo1esh8">
                  Sends to {location.name} on {location.phone}
                </p>
              </div>

              <button type="submit" className="glow-pink inline-flex w-full items-center justify-center gap-2 rounded-full bg-lotus-500 px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-lotus-600" data-id="element-212" data-mp="2nvz9l9ozd644">
                <MessageSquareIcon className="h-5 w-5" aria-hidden="true" data-id="element-213" data-mp="17h7egp0g9kr4" />
                Text this venue
              </button>

              {sent && <p role="status" className="flex items-center justify-center gap-2 text-sm text-lotus-400" data-id="element-214" data-mp="3f6bx58xoz3o7">
                  <CheckCircle2Icon className="h-4 w-4" aria-hidden="true" data-id="element-215" data-mp="3o68oxjfdoinh" />
                  Your messaging app should now be open — press send.
                </p>}
            </form>
          </div>

          <div data-id="element-216" data-mp="1qxgigcgfdsu">
            <h2 className="text-3xl font-bold tracking-tight text-white" data-id="element-217" data-mp="1pcksneelote">Our Locations</h2>
            <ul className="mt-8 space-y-5" data-id="element-218" data-mp="6opdhoryhq3w">
              {LOCATIONS.map(loc => <li key={loc.slug} className="rounded-2xl border border-white/5 bg-ink-850 p-6" data-id="element-219" data-mp="aqkip4d0cnmy">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-lotus-500" data-id="element-220" data-mp="fqowunqg16a0">
                    {loc.suburb}
                  </p>
                  <h3 className="mt-2 text-lg font-bold text-white" data-id="element-221" data-mp="2cka1nvhm4lqs">
                    <Link to={`/locations/${loc.slug}`} className="hover:text-lotus-500" data-id="element-222" data-mp="2hibh86fty2xi">
                      {loc.name}
                    </Link>
                  </h3>
                  <p className="mt-4 flex items-start gap-3 text-sm text-neutral-300" data-id="element-223" data-mp="3ftw7lqec17tb">
                    <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-224" data-mp="3tsvxf1dkzsr2" />
                    {loc.address}
                  </p>
                  <p className="mt-3 flex items-start gap-3 text-sm text-neutral-300" data-id="element-225" data-mp="7l4cgp0hh9k6">
                    <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-226" data-mp="2ncog62ym7l9" />
                    <a href={`tel:${loc.phone.replace(/\s/g, '')}`} className="hover:text-lotus-500" data-id="element-227" data-mp="gg2agaihncid">
                      {loc.phone}
                    </a>
                  </p>
                  <p className="mt-3 flex items-start gap-3 text-sm text-neutral-300" data-id="element-228" data-mp="ledj4hx1rgz0">
                    <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-229" data-mp="14ykpm4lr9zar" />
                    {loc.hours}
                  </p>
                </li>)}
            </ul>
          </div>
        </div>
      </section>
    </main>;
}